import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconPlayerStopFilled, IconLoader2, IconArrowUp, IconChevronDown, IconTrash } from '@tabler/icons-react';
import { C, RADIUS } from '../theme';
import { MicIcon } from './InlineIcons';
import { apiFetch } from '../lib/api';
import { currencySymbol } from '../lib/money';
import { useOrg as _useOrgForCurrency } from './OrgContext';
import { VoiceModalContext, useVoice } from './voiceContext';
import { AskProvider } from './AskPanel';
import { isRedesign } from '../lib/redesign';
import { homeKindFor } from '../redesign/company';

// App-level voice + text assistant. It lives in ONE provider mounted above the
// router, rendered as a PERSISTENT bottom-right surface present on every screen:
//   - collapsed → a floating mic button (FAB)
//   - expanded  → a docked conversation panel (states, transcript, log, text input)
// The panel is docked (no full-screen overlay), so routes can change underneath
// it while the session — conversation memory, listening/speaking state — stays
// alive. Minimizing keeps the session; only an explicit Clear (or logout) resets.
//
// Two input modes into ONE pipeline: speak it OR type it. Read-only today: it
// answers questions from real data and speaks the answer; commands are
// recognized but not executed yet. Bilingual (Italian/English).
//
// The voice engine is deliberately imperative (refs, not state) so the anti-bleed
// / barge-in / Stop logic can't be tripped up by stale React closures.


const isItalianLang = (l) => /ital/i.test(String(l || ''));

export { useVoice };

// status: 'idle' | 'listening' | 'thinking' | 'speaking'
// The permission each assistant action needs (mirrors server/permissions.js; the server enforces it).
const VOICE_ACTION_PERMISSION = {
  adjust_inventory: 'inventory.edit',
  record_stock_out: 'inventory.edit',
  build_product: 'inventory.edit',
  approve_bill: 'cases.decide',
  reorder: 'po.send',
};

// Pure agents on the redesigned site get Ask (the docked panel, server tools, approval cards); every other company keeps the voice window below, unchanged.
export function VoiceModalProvider({ children }) {
  const { capabilities } = _useOrgForCurrency();
  if (isRedesign() && homeKindFor(capabilities) === 'agent') return <AskProvider>{children}</AskProvider>;
  return <LegacyVoiceProvider>{children}</LegacyVoiceProvider>;
}

function LegacyVoiceProvider({ children }) {
  const { can } = _useOrgForCurrency();
  const [expanded, setExpanded] = useState(false); // collapsed FAB vs docked panel
  const [status, setStatus] = useState('idle');
  const [input, setInput] = useState(''); // editable field: heard speech OR typed text
  const [awaitingSend, setAwaitingSend] = useState(false); // a voice COMMAND is waiting in the field for review
  const [turns, setTurns] = useState([]); // [{ who: 'you'|'assistant', text }]
  const [command, setCommand] = useState(false); // true once a non-actionable COMMAND was recognized
  const [pendingAction, setPendingAction] = useState(null); // { action, params, prepared } awaiting Approve
  const [executing, setExecuting] = useState(false); // a write is in flight
  const [undo, setUndo] = useState(null); // { auditId } — Undo available for ~5s
  const [error, setError] = useState(null);
  const navigate = useNavigate(); // route underneath the persistent panel
  const undoTimerRef = useRef(null);

  // Imperative engine state.
  const statusRef = useRef('idle');
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const historyRef = useRef([]); // [{ role, content }] sent to the brain for memory
  const languageRef = useRef(null); // last detected spoken language
  const reviewRef = useRef(null); // a voice COMMAND awaiting Send: { text, data }
  const inputRef = useRef(null); // the text field, so openVoice can focus it

  // TTS playback (one shared <audio>), with abort + token guards for barge-in.
  const ttsAudioRef = useRef(null);
  const ttsUrlRef = useRef(null);
  const ttsAbortRef = useRef(null);
  const speechTokenRef = useRef(0);

  if (!ttsAudioRef.current && typeof Audio !== 'undefined') {
    ttsAudioRef.current = new Audio();
  }

  const setStat = useCallback((s) => {
    statusRef.current = s;
    setStatus(s);
  }, []);

  const addTurn = useCallback((who, text) => {
    if (text && text.trim()) setTurns((t) => [...t, { who, text: text.trim() }]);
  }, []);
  const pushHistory = useCallback((role, content) => {
    if (content && content.trim()) historyRef.current.push({ role, content: content.trim() });
  }, []);

  // --- TTS -----------------------------------------------------------------
  const clearTts = useCallback(() => {
    if (ttsAbortRef.current) {
      try { ttsAbortRef.current.abort(); } catch (_) {}
      ttsAbortRef.current = null;
    }
    const a = ttsAudioRef.current;
    if (a) {
      try { a.pause(); } catch (_) {}
      a.onended = null;
      a.onerror = null;
      a.removeAttribute('src');
    }
    if (ttsUrlRef.current) {
      URL.revokeObjectURL(ttsUrlRef.current);
      ttsUrlRef.current = null;
    }
  }, []);

  // Silence the assistant immediately. Used by Stop and by barge-in. Idempotent.
  const shutUp = useCallback(() => {
    speechTokenRef.current += 1; // invalidate any in-flight fetch / playback
    clearTts();
    if (statusRef.current === 'speaking') setStat('idle');
  }, [clearTts, setStat]);

  const speak = useCallback(async (text) => {
    if (!text || !text.trim()) return;
    const myToken = ++speechTokenRef.current;
    clearTts();
    setStat('speaking');

    const done = () => {
      if (myToken !== speechTokenRef.current) return; // superseded by a newer utterance
      clearTts();
      if (statusRef.current === 'speaking') setStat('idle');
    };

    try {
      ttsAbortRef.current = new AbortController();
      const res = await apiFetch('/api/voice/speak', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
        signal: ttsAbortRef.current.signal,
      });
      if (myToken !== speechTokenRef.current) return; // interrupted during fetch
      if (!res.ok) { done(); return; } // TTS failed → behave like it ended (text already shown)
      const blob = await res.blob();
      if (myToken !== speechTokenRef.current) return; // interrupted while reading body

      const url = URL.createObjectURL(blob);
      ttsUrlRef.current = url;
      const a = ttsAudioRef.current;
      a.src = url;
      a.onended = done;
      a.onerror = done;
      a.play().catch((e) => { if (e && e.name === 'NotAllowedError') done(); });
    } catch (err) {
      if (err && err.name === 'AbortError') return; // interrupted
      done();
    }
  }, [clearTts, setStat]);

  // --- The brain -----------------------------------------------------------
  // Classify one message. Returns the parsed result, or null on error (which it
  // surfaces). Does NOT display anything — callers decide auto-send vs review.
  const runClassify = useCallback(async (text) => {
    setStat('thinking');
    try {
      const res = await apiFetch('/api/voice/classify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, history: historyRef.current, language: languageRef.current }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.message || 'Something went wrong understanding that.');
        setStat('idle');
        return null;
      }
      return data;
    } catch (err) {
      setError('Network error: ' + err.message);
      setStat('idle');
      return null;
    }
  }, [setStat]);

  // Display + speak one classified turn (shared by typed and spoken input).
  const proceed = useCallback((data, text) => {
    addTurn('you', text);
    pushHistory('user', text);

    if (data.type === 'CLARIFY') {
      const q = data.clarifyQuestion || 'Could you clarify?';
      addTurn('assistant', q);
      pushHistory('assistant', q);
      speak(q);
      return;
    }

    if (data.type === 'NAVIGATE') {
      const answer = data.answer || 'Opening…';
      addTurn('assistant', answer);
      pushHistory('assistant', answer);
      speak(answer);
      // Navigate underneath the persistent panel — the session stays alive, the
      // panel stays docked. No reset, no collapse.
      if (data.route) navigate(data.route);
      return;
    }

    // An actionable command (e.g. adjust_inventory) → show a confirm card and
    // wait for explicit Approve. The write happens only then, server-side.
    if (data.type === 'COMMAND' && data.confirm && data.action) {
      const needed = VOICE_ACTION_PERMISSION[data.action];
      if (!needed || !can(needed)) {
        const denied = "You don't have permission to do that.";
        addTurn('assistant', denied);
        pushHistory('assistant', denied);
        speak(denied);
        return;
      }
      const q = data.answer || 'Confirm?';
      addTurn('assistant', q);
      pushHistory('assistant', q);
      setPendingAction({ action: data.action, params: data.params || {}, prepared: data.prepared || {} });
      speak(q);
      return;
    }

    const answer = data.answer || '—';
    addTurn('assistant', answer);
    pushHistory('assistant', answer);
    // A non-actionable COMMAND is recognized but not executed — calm note, no button.
    if (data.type === 'COMMAND') setCommand(true);
    speak(answer);
  }, [addTurn, pushHistory, speak, navigate, can]);

  // --- Approve / cancel / undo a write -------------------------------------
  const approveAction = useCallback(async () => {
    const pa = pendingAction;
    if (!pa) return;
    setPendingAction(null);
    setCommand(false); // clear any stale "recognized a change" note — this IS a live write
    setExecuting(true);
    const it = isItalianLang(languageRef.current);
    try {
      const res = await apiFetch('/api/voice/actions/execute', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: pa.action, params: pa.params, language: languageRef.current }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.message || (it ? 'Modifica non riuscita.' : 'That change failed.'));
        return;
      }
      const msg = data.summary || (it ? 'Fatto.' : 'Done.');
      addTurn('assistant', msg);
      pushHistory('assistant', msg);
      speak(msg);
      if (data.auditId) {
        if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
        setUndo({ auditId: data.auditId });
        undoTimerRef.current = setTimeout(() => setUndo(null), 6000);
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setExecuting(false);
    }
  }, [pendingAction, addTurn, pushHistory, speak]);

  const cancelAction = useCallback(() => {
    setPendingAction(null);
    const it = isItalianLang(languageRef.current);
    const msg = it ? 'Va bene, annullato.' : 'Okay, cancelled.';
    addTurn('assistant', msg);
    pushHistory('assistant', msg);
  }, [addTurn, pushHistory]);

  const runUndo = useCallback(async () => {
    const u = undo;
    if (!u) return;
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    setUndo(null);
    try {
      const res = await apiFetch('/api/voice/actions/undo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ auditId: u.auditId, language: languageRef.current }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.message || 'Undo failed.');
        return;
      }
      addTurn('assistant', data.message);
      pushHistory('assistant', data.message);
      speak(data.message);
    } catch (err) {
      setError('Network error: ' + err.message);
    }
  }, [undo, addTurn, pushHistory, speak]);

  // --- Recording -----------------------------------------------------------
  const sendAudio = useCallback(async (blob) => {
    const fd = new FormData();
    fd.append('audio', blob, 'audio.webm');
    setStat('thinking');
    try {
      const res = await apiFetch('/api/voice/transcribe', { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok) {
        setError(data.message || 'Could not hear that — try again.');
        setStat('idle');
        return;
      }
      const heard = (data.transcript || '').trim();
      languageRef.current = data.language || languageRef.current;
      if (!heard) {
        setError("I didn't catch that — tap the mic or type instead.");
        setStat('idle');
        return;
      }
      // The transcript lands in the editable field so the user can correct it.
      setInput(heard);
      const result = await runClassify(heard);
      if (!result) return;

      if (result.type === 'COMMAND') {
        // A command must NOT auto-send — leave it in the field for review/edit.
        reviewRef.current = { text: heard, data: result };
        setAwaitingSend(true);
        setStat('idle');
      } else {
        // Questions (and clarifications) auto-send — frictionless.
        reviewRef.current = null;
        setAwaitingSend(false);
        setInput('');
        proceed(result, heard);
      }
    } catch (err) {
      setError('Network error: ' + err.message);
      setStat('idle');
    }
  }, [runClassify, proceed, setStat]);

  const startRecording = useCallback(async () => {
    setError(null);
    reviewRef.current = null;
    setAwaitingSend(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const mr = new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        await sendAudio(new Blob(chunksRef.current, { type: 'audio/webm' }));
      };
      mr.start();
      setInput('');
      setCommand(false);
      setPendingAction(null);
      setStat('listening');
    } catch (err) {
      setError('Microphone error: ' + err.message);
      setStat('idle');
    }
  }, [sendAudio, setStat]);

  // The single mic tap handler — derives behaviour from the current status so
  // recording, speaking and thinking can never race the same button.
  const onMicTap = useCallback(async () => {
    const s = statusRef.current;
    if (s === 'thinking') return; // busy; ignore
    if (s === 'speaking') {
      // Barge-in: silence the assistant first, THEN record. The mic never goes
      // live while TTS is still playing (anti-bleed).
      shutUp();
      await startRecording();
      return;
    }
    if (s === 'listening') {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      return;
    }
    await startRecording();
  }, [shutUp, startRecording]);

  // --- Text input (front door #2) + the review-gate Send for voice commands --
  const onSend = useCallback(async () => {
    const text = input.trim();
    if (!text) return;
    if (statusRef.current === 'listening' || statusRef.current === 'thinking') return;
    if (statusRef.current === 'speaking') shutUp(); // submitting also interrupts TTS

    setError(null);
    setCommand(false);

    let data;
    const rev = reviewRef.current;
    if (rev && rev.text === text) {
      data = rev.data; // reuse the unedited voice-command classification
    } else {
      data = await runClassify(text); // typed input, or the user edited the voice text
    }
    reviewRef.current = null;
    setAwaitingSend(false);
    if (!data) return;
    setInput('');
    proceed(data, text);
  }, [input, runClassify, proceed, shutUp]);

  // --- Stop active audio/recording WITHOUT clearing the conversation --------
  const quietEngine = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      try { mediaRecorderRef.current.stop(); } catch (_) {}
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    shutUp();
    reviewRef.current = null;
    setAwaitingSend(false);
    setStat('idle');
  }, [shutUp, setStat]);

  // Full reset — wipes the conversation. Used by explicit Clear and on unmount
  // (e.g. logout). NOT used when minimizing, so memory survives.
  const reset = useCallback(() => {
    quietEngine();
    historyRef.current = [];
    languageRef.current = null;
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    setTurns([]);
    setInput('');
    setCommand(false);
    setPendingAction(null);
    setUndo(null);
    setError(null);
  }, [quietEngine]);

  // --- Open / minimize -----------------------------------------------------
  // openVoice expands (or focuses) the persistent surface — it never resets, so
  // the existing Home mic button and the nav mic both reopen the SAME session.
  const openVoice = useCallback(() => {
    setError(null);
    setExpanded(true);
    // focus the field once the panel has mounted/painted
    requestAnimationFrame(() => inputRef.current && inputRef.current.focus());
  }, []);
  // Minimize back to the FAB: stop live audio but KEEP the conversation.
  const collapse = useCallback(() => {
    quietEngine();
    setPendingAction(null);
    setExpanded(false);
  }, [quietEngine]);

  // Safety: if the provider unmounts (e.g. logout), stop any audio/recording.
  useEffect(() => () => reset(), [reset]);

  // Expose whether a conversation is alive so the header/Home mic triggers can
  // show the green "still here" dot. The panel itself is the only on-screen
  // surface the provider renders — there is no separate floating mic button.
  const ctxValue = useMemo(
    () => ({ openVoice, voiceActive: turns.length > 0 }),
    [openVoice, turns.length]
  );

  return (
    <VoiceModalContext.Provider value={ctxValue}>
      {children}
      {expanded && (
        <VoicePanel
          status={status}
          input={input}
          onInputChange={setInput}
          inputRef={inputRef}
          awaitingSend={awaitingSend}
          turns={turns}
          command={command}
          pendingAction={pendingAction}
          executing={executing}
          undo={undo}
          error={error}
          onMicTap={onMicTap}
          onStop={shutUp}
          onSend={onSend}
          onApprove={approveAction}
          onCancelAction={cancelAction}
          onUndo={runUndo}
          onClear={reset}
          onMinimize={collapse}
        />
      )}
    </VoiceModalContext.Provider>
  );
}

// --- Expanded docked panel -------------------------------------------------

const STATE_COPY = {
  idle: 'Tap the mic to speak, or type below',
  listening: 'Listening… tap to stop',
  thinking: 'Thinking…',
  speaking: 'Speaking… tap to interrupt',
};

function VoicePanel({
  status, input, onInputChange, inputRef, awaitingSend, turns, command, error,
  pendingAction, executing, undo, onMicTap, onStop, onSend, onApprove, onCancelAction, onUndo,
  onClear, onMinimize,
}) {
  const logRef = useRef(null);
  // A prepared action's amount is real money — render it in the org's currency.
  const { currency: _voiceCurrency } = _useOrgForCurrency();
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [turns, command, error, pendingAction, undo]);

  const listening = status === 'listening';
  const speaking = status === 'speaking';
  const thinking = status === 'thinking';
  const sendDisabled = !input.trim() || listening || thinking;

  return (
    <div
      role="dialog"
      aria-label="Floxir voice assistant"
      style={{
        position: 'fixed',
        right: 20,
        bottom: 20,
        width: 'min(400px, calc(100vw - 32px))',
        maxHeight: 'calc(100vh - 40px)',
        background: C.card,
        border: `1px solid ${C.border}`,
        borderRadius: RADIUS.lg,
        boxShadow: '0 16px 48px rgba(0,0,0,0.22)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1000,
        overflow: 'hidden',
      }}
    >
      <style>{`
        @keyframes floxir-pulse { 0%{box-shadow:0 0 0 0 rgba(196,96,29,0.45)} 70%{box-shadow:0 0 0 18px rgba(196,96,29,0)} 100%{box-shadow:0 0 0 0 rgba(196,96,29,0)} }
        @keyframes floxir-spin { to { transform: rotate(360deg) } }
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px 8px' }}>
        <div style={{ fontSize: 16, fontWeight: 500, color: C.textDark }}>Ask Floxir</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {turns.length > 0 && (
            <button
              type="button"
              aria-label="Clear conversation"
              onClick={onClear}
              style={iconBtnStyle}
            >
              <IconTrash size={15} color={C.textMuted} />
            </button>
          )}
          <button
            type="button"
            aria-label="Minimize"
            onClick={onMinimize}
            style={iconBtnStyle}
          >
            <IconChevronDown size={17} color={C.textMuted} />
          </button>
        </div>
      </div>

      {/* Conversation log */}
      <div
        ref={logRef}
        style={{
          flex: '1 1 auto',
          minHeight: 0,
          maxHeight: '46vh',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          padding: turns.length || error || command ? '6px 14px 12px' : '0 14px',
        }}
      >
        {turns.map((t, i) => (
          <div
            key={i}
            style={{
              alignSelf: t.who === 'you' ? 'flex-end' : 'flex-start',
              maxWidth: '85%',
              background: t.who === 'you' ? C.amberLight : C.bg,
              border: `1px solid ${t.who === 'you' ? C.amberBorder : C.border}`,
              color: C.textDark,
              fontSize: 14,
              lineHeight: 1.45,
              padding: '8px 12px',
              borderRadius: RADIUS.md,
            }}
          >
            <div style={{ fontSize: 11, color: C.textFaint, marginBottom: 2 }}>
              {t.who === 'you' ? 'You' : 'Floxir'}
            </div>
            {t.text}
          </div>
        ))}

        {command && (
          <div
            style={{
              alignSelf: 'flex-start',
              maxWidth: '90%',
              background: C.amberLight,
              border: `1px solid ${C.amberBorder}`,
              borderRadius: RADIUS.md,
              padding: '10px 12px',
              fontSize: 13,
              color: C.amber,
              lineHeight: 1.45,
            }}
          >
            I understood a change request — but making changes here isn't switched on yet. It's coming.
          </div>
        )}

        {error && (
          <div
            style={{
              alignSelf: 'stretch',
              background: C.redLight,
              border: `1px solid ${C.redBorder}`,
              borderRadius: RADIUS.md,
              padding: '10px 12px',
              fontSize: 13,
              color: C.red,
              lineHeight: 1.45,
            }}
          >
            {error}
          </div>
        )}
      </div>

      {/* Confirm card — explicit approval gate for an actionable write */}
      {pendingAction && (
        <div style={{ margin: '4px 14px 6px', border: `1px solid ${C.amberBorder}`, background: C.amberLight, borderRadius: RADIUS.md, padding: '12px 14px' }}>
          <div style={{ fontSize: 11, color: C.textFaint, marginBottom: 4 }}>Confirm</div>
          {pendingAction.prepared.kind === 'approve_bill' ? (
            <>
              <div style={{ fontSize: 14, color: C.textDark, fontWeight: 500 }}>
                Approve invoice {pendingAction.prepared.invoiceNumber}
              </div>
              <div style={{ fontSize: 14, color: C.textDark, margin: '6px 0 12px' }}>
                {pendingAction.prepared.supplier}
                {pendingAction.prepared.amount != null && (
                  <span style={{ color: C.textMuted }}> · {currencySymbol(_voiceCurrency)}{Number(pendingAction.prepared.amount).toFixed(2)}</span>
                )}
              </div>
            </>
          ) : pendingAction.prepared.kind === 'record_stock_out' ? (
            <>
              <div style={{ fontSize: 14, color: C.textDark, fontWeight: 500 }}>
                Record stock leaving
              </div>
              <div style={{ fontSize: 15, color: C.textDark, margin: '6px 0 2px' }}>
                <span style={{ fontWeight: 600, color: C.orange }}>{pendingAction.prepared.quantity}</span>
                {' × '}
                {pendingAction.prepared.name}
                {pendingAction.prepared.sku && (
                  <span style={{ color: C.textMuted, fontWeight: 400 }}> ({pendingAction.prepared.sku})</span>
                )}
              </div>
              <div style={{ fontSize: 14, color: C.textDark, margin: '4px 0 12px' }}>
                {pendingAction.prepared.warehouse_name && (
                  <span style={{ color: C.textMuted }}>{pendingAction.prepared.warehouse_name} · </span>
                )}
                {pendingAction.prepared.current_qty} <span style={{ color: C.textFaint }}>→</span>{' '}
                <span style={{ fontWeight: 600, color: C.orange }}>{pendingAction.prepared.after_qty}</span>
              </div>
            </>
          ) : pendingAction.prepared.kind === 'reorder' ? (
            <>
              <div style={{ fontSize: 14, color: C.textDark, fontWeight: 500 }}>Prepare draft order</div>
              <div style={{ fontSize: 15, color: C.textDark, margin: '6px 0 2px' }}>
                <span style={{ fontWeight: 600, color: C.orange }}>{pendingAction.prepared.quantity}</span>
                {' × '}
                {pendingAction.prepared.name}
                {pendingAction.prepared.sku && (
                  <span style={{ color: C.textMuted, fontWeight: 400 }}> ({pendingAction.prepared.sku})</span>
                )}
              </div>
              <div style={{ fontSize: 13, color: C.textMuted, marginBottom: 12 }}>
                from {pendingAction.prepared.supplier}
              </div>
            </>
          ) : pendingAction.prepared.kind === 'build_product' ? (
            <>
              <div style={{ fontSize: 14, color: C.textDark, fontWeight: 500 }}>
                Make <span style={{ color: C.orange, fontWeight: 600 }}>{pendingAction.prepared.qty}</span> × {pendingAction.prepared.name}
              </div>
              {!pendingAction.prepared.can_build && (
                <div style={{ fontSize: 12.5, color: C.amber, margin: '4px 0 0' }}>
                  Not enough for {pendingAction.prepared.requested_qty} — building the {pendingAction.prepared.qty} you can.
                </div>
              )}
              <div style={{ fontSize: 12.5, color: C.textMuted, margin: '8px 0 2px' }}>Consumes</div>
              {(pendingAction.prepared.requirements || []).map((r, i) => (
                <div key={i} style={{ fontSize: 13.5, color: C.textDark }}>
                  {r.required}{r.unit ? ` ${r.unit}` : ''} {r.name}
                </div>
              ))}
              <div style={{ fontSize: 14, color: C.textDark, margin: '8px 0 12px' }}>
                Produces <span style={{ fontWeight: 600, color: C.green }}>+{pendingAction.prepared.qty}</span> {pendingAction.prepared.finished.name}
                {' '}<span style={{ color: C.textMuted }}>({pendingAction.prepared.finished.current} → {pendingAction.prepared.finished.after})</span>
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 14, color: C.textDark, fontWeight: 500 }}>
                {pendingAction.prepared.name}
                {pendingAction.prepared.sku && (
                  <span style={{ color: C.textMuted, fontWeight: 400 }}> ({pendingAction.prepared.sku})</span>
                )}
              </div>
              <div style={{ fontSize: 15, color: C.textDark, margin: '6px 0 12px' }}>
                {pendingAction.prepared.current_qty} <span style={{ color: C.textFaint }}>→</span>{' '}
                <span style={{ fontWeight: 600, color: C.orange }}>{pendingAction.prepared.target_qty}</span>
              </div>
            </>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onCancelAction}
              disabled={executing}
              style={{ padding: '8px 14px', borderRadius: RADIUS.md, background: C.card, border: `1px solid ${C.border}`, color: C.textDark, fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onApprove}
              disabled={executing}
              style={{ padding: '8px 16px', borderRadius: RADIUS.md, background: C.orange, color: '#fff', border: 'none', fontSize: 13, fontWeight: 500, cursor: executing ? 'default' : 'pointer', opacity: executing ? 0.6 : 1, fontFamily: 'inherit' }}
            >
              {executing ? 'Working…' : 'Approve'}
            </button>
          </div>
        </div>
      )}

      {/* Undo (≈5s) after a write */}
      {undo && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2px 0 6px' }}>
          <button
            type="button"
            onClick={onUndo}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 16px', borderRadius: RADIUS.md, background: C.textDark, color: '#fff', border: 'none', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }}
          >
            Undo
          </button>
        </div>
      )}

      {/* Mic + state */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 7, padding: '10px 0 2px' }}>
        <button
          type="button"
          onClick={onMicTap}
          disabled={thinking}
          aria-label={STATE_COPY[status]}
          style={{
            width: 64, height: 64, borderRadius: '50%',
            background: thinking ? C.bg : C.orange,
            border: listening ? `4px solid ${C.amberBorder}` : `4px solid ${C.bg}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: thinking ? 'default' : 'pointer', padding: 0,
            boxShadow: '0 4px 16px rgba(196,96,29,0.35)',
            animation: listening ? 'floxir-pulse 1.6s infinite' : 'none',
          }}
        >
          {thinking ? (
            <IconLoader2 size={26} color={C.orange} style={{ animation: 'floxir-spin 0.9s linear infinite' }} />
          ) : (
            <MicIcon size={28} color="#fff" />
          )}
        </button>
        <div style={{ fontSize: 13, color: awaitingSend ? C.amber : C.textMuted, textAlign: 'center', padding: '0 12px' }}>
          {awaitingSend ? 'Looks like a command — check the text and press Send.' : STATE_COPY[status]}
        </div>

        {speaking && (
          <button
            type="button"
            onClick={onStop}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              marginTop: 2, padding: '6px 14px', borderRadius: RADIUS.md,
              background: C.card, border: `1px solid ${C.border}`,
              color: C.textDark, fontSize: 13, fontWeight: 500, cursor: 'pointer',
            }}
          >
            <IconPlayerStopFilled size={14} color={C.red} /> Stop
          </button>
        )}
      </div>

      {/* Text input (front door #2) */}
      <div style={{ padding: '12px 14px 8px' }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            ref={inputRef}
            type="text"
            value={input}
            autoFocus
            onChange={(e) => onInputChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                onSend();
              }
            }}
            placeholder="Ask or type a question…"
            style={{
              flex: 1,
              minWidth: 0,
              fontFamily: 'inherit',
              fontSize: 14,
              color: C.textDark,
              background: C.card,
              border: `1px solid ${awaitingSend ? C.amberBorder : C.border}`,
              borderRadius: RADIUS.md,
              padding: '10px 12px',
              outline: 'none',
            }}
          />
          <button
            type="button"
            onClick={onSend}
            disabled={sendDisabled}
            aria-label="Send"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              padding: '0 16px', height: 40, borderRadius: RADIUS.md,
              background: sendDisabled ? C.border : C.orange,
              color: '#fff', border: 'none',
              fontSize: 14, fontWeight: 500,
              cursor: sendDisabled ? 'default' : 'pointer',
            }}
          >
            <IconArrowUp size={16} color="#fff" /> Send
          </button>
        </div>
        <div style={{ fontSize: 11, color: C.textFaint, marginTop: 8, textAlign: 'center' }}>
          Ask in Italian or English
        </div>
      </div>
    </div>
  );
}

const iconBtnStyle = {
  width: 30,
  height: 30,
  borderRadius: '50%',
  background: C.bg,
  border: `1px solid ${C.border}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  padding: 0,
};
